import {
    Button,
    Card,
    Col,
    Input,
    Row,
    Table,
    Typography
} from "antd";
import { SortOrder } from "antd/lib/table/interface";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { SalesforceActionId } from "../../../constants/salesforceActionId";
import { CompanyEnabledLog } from "../../../models/company-enabled-log-interface";
import { SalesforceWebhookLog } from "../../../models/salesforce-webhook-logs-interface";
import { CustomerServiceQueries } from "../../../queries/customer-service-queries";

const { Title } = Typography;

type Props = {
  numDays: number;
};

const CPQWebhookTab: React.FC<Props> = ({
  numDays
}) => {
  const history = useHistory();
  const [search, setSearch] = useState<string|null>(null);
  const [filteredData, setFilteredData] = useState<SalesforceWebhookLog[]|null>(null);
  const useSalesforceWebhookLogs = CustomerServiceQueries.useSalesforceWebhookLogs(numDays);
  
  useEffect(() => {
    if(!!search) {
      const searchLower = search.toLocaleLowerCase();
      const data = useSalesforceWebhookLogs.data?.filter(x => x.companyName?.toLocaleLowerCase().includes(searchLower) ||
        x.payload?.toLocaleLowerCase().includes(searchLower));
      setFilteredData(data ?? []);
    } else {
      setFilteredData(null);
    }
  }, [search])
  
  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      render: (companyName: string, record: CompanyEnabledLog) => {
        return (
          <Button
            type="link"
            href={`/admin/company/${record.companyId}`}
            onClick={(e) => {
              e.preventDefault();
              history.push(`/admin/company/${record.companyId}`);
            }}
          >
            {companyName}
          </Button>
        );
      },
      sorter: {
        compare: (a: CompanyEnabledLog, b: CompanyEnabledLog) => (a.companyName ?? "").localeCompare((b.companyName ?? "")) 
      }
    },
    {
      title: "Payload",
      dataIndex: "payload",
      key: "payload",
      render: (payload: string) => {
        return (
          <div style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}>
            {payload}
          </div>
        )
      }
    },
    {
        title: "Action",
        dataIndex: "webhookActionId",
        key: "webhookActionId",
        render: (webhookActionId: number, record: SalesforceWebhookLog) => {
          return record.webhookActionName;
        },
        filters: [...Object.values(SalesforceActionId).filter(x => isNaN(x)).map((value, index) => {
          return { text: value.replace(/([a-z])([A-Z])/g, `$1 $2`), value: index + 1 }
        })],
        onFilter: (value: number, record: SalesforceWebhookLog) => {
          return record.webhookActionId === value;
        }
      },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => {
        return new Date(date + "Z").toLocaleString();
      },
      sorter: {
        compare: (a, b) => {
            let d1 = a.date ?? new Date("01/01/1000");
            let d2 = b.date ?? new Date("01/01/1000");
            return moment(d1).diff(moment(d2));
        }
      },
      defaultSortOrder: 'descend' as SortOrder,
      sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    },
  ];

  return (
    <Card style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col flex={"auto"}>
        </Col>
        <Col>
          <Input
            placeholder="Search Logs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col span={24}>
          <Table
            className="full-width"
            rowKey={(data) => data.id}
            dataSource={filteredData ?? useSalesforceWebhookLogs.data ?? []}
            columns={columns}
            loading={useSalesforceWebhookLogs.isLoading}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default CPQWebhookTab;
